<template>
  <div class="texto-wrapper mt-4">
    <h6 class="font-weight-bold">
      Cobertura populacional do exame citopatológico do colo do útero (exame Papanicolau)
    </h6>
    <p>
      Uma das maneiras de avaliar uma política de saúde é verificar se as metas esperadas estão
      sendo alcançadas. Indicadores de cobertura populacional são extremamente úteis para essa
      função, pois informam quantas pessoas realmente estão sendo atendidas por essa política.
    </p>
    <p>
      Diversas ações que fazem parte de uma política de saúde são direcionadas para uma determinada
      população-alvo que, por sua vez, foi determinada por meio de várias evidências e estudos
      científicos utilizados para embasar cada ação.
    </p>
    <p>
      No caso do exame citopatológico do colo do útero, também conhecido como exame Papanicolau, a
      população-alvo é formada pelas mulheres sexualmente ativas na faixa etária de 25 a 64 anos de
      idade, conforme explicado no texto.
    </p>
    <p>
      Uma vez que a coleta do exame pode ser realizada a cada três anos, a meta de cobertura anual
      de 80% é aplicada sobre um terço (33%) da população-alvo. Aos 33% são acrescidos 7% em razão
      dos novos exames anuais e das repetições necessárias, totalizando 40% da população-alvo. Para
      avaliar se um município atingiu ou não a meta de cobertura, utilizam-se os seguintes
      parâmetros:
    </p>
    <ul>
      <li>
        Menos de 50% de cobertura = <span class="meta meta--ruim">MUITO ABAIXO DA META</span>
      </li>
      <li>
        Entre 50% e 79% de cobertura = <span class="meta meta--medio">ABAIXO DA META</span>
      </li>
      <li>
        80% até 100% = <span class="meta meta--boa">META ATINGIDA</span>
      </li>
      <li>
        Acima de 100% de cobertura = <span class="meta meta--ruim">MUITO ACIMA DA META</span>
      </li>
    </ul>
    <p>
      Para cada uma dessas situações, são fornecidos planos de ação para auxiliar o gestor
      municipal a manter ou atingir as metas de cobertura.
    </p>
    <h6 class="font-weight-bold">Como o indicador é produzido</h6>
    <p>
      O Censo Demográfico produzido pelo Instituto Brasileiro de Geografia e Estatística (IBGE)
      fornece várias informações sobre a população de cada município brasileiro, entre elas o
      gênero, a idade, bem como a estimativa de população para os anos entre os censos - que
      ocorrem a cada dez anos.
    </p>
    <p>
      Além disso, a Agência Nacional de Saúde Suplementar (ANS) fornece, para cada município
      brasileiro, o número de pessoas que possuem plano privado de saúde no site do TABNET-ANS.
    </p>
    <p>
      Com isso, é possível determinar qual é a população de cada município que teria benefício com
      a ação pública de saúde para coleta do exame preventivo do câncer de colo uterino, ou seja,
      a população-alvo, por meio do cálculo:
    </p>
    <div class="border border-dark text-center mb-3">
      <p class="small-text mt-2 mb-2">
        <b>População-alvo</b> = (população feminina IBGE de 25 a 64 anos de idade -
        população feminina ANS de 25 a 64 anos de idade)  x 40%
      </p>
    </div>
    <p>
      É importante ressaltar que NÃO existe justificativa clínica para exame de rastreamento em
      pessoas com colo do útero fora da faixa etária especificada. Em especial nas faixas etárias
      abaixo de 10 anos de idade e entre 10 e 19 anos, por se tratarem de crianças e adolescentes
      que só podem ser avaliadas ginecologicamente por questões diagnósticas, nunca por
      rastreamento.
    </p>
    <p>
      Por outro lado, os Sistemas de Informação em Saúde (SIS) governamentais, sem identificar
      nenhuma paciente e, portanto, preservando a privacidade e a intimidade, além de respeitar
      a Lei Geral de Proteção de dados (LGPD), fornecem a informação de quantas pacientes
      tiveram o exame coletado nas unidades de saúde de cada município. Essa informação é
      disponibilizada de forma pública pelo Departamento de Informática do SUS (DATASUS) no site
      do TABNET-SUS. O TABNET também dá acesso aos dados do IBGE, facilitando o acesso às
      informações demográficas de gênero e idade.
    </p>
    <p>
      Com isso é possível determinar qual foi a população efetivamente atingida pela ação de saúde
      pública, ou seja, a população coberta pela ação.
    </p>
    <p>
      A partir da relação entre população coberta e população-alvo, pode-se obter o percentual de
      cobertura populacional da coleta do exame preventivo do câncer de colo uterino:
    </p>
    <div class="border border-dark text-center mb-3">
      <p class="small-text mt-2 mb-2">
        <b>Cobertura</b> = população coberta / população-alvo x 100
      </p>
    </div>
    <p>
      Todos os dados utilizados na construção desse indicador são públicos e estão disponíveis nos
      sites das instituições citadas no texto.
    </p>
    <p>Referências</p>
    <p>
      1. Brasil. Ministério da Saúde. Instituto Nacional de Câncer José Alencar Gomes da Silva
      (INCA). Diretrizes brasileiras para o rastreamento do câncer do colo do útero. 2. ed. rev.
      ampl. atual. Rio de Janeiro: INCA, 2016:114p. Disponível em:
      <a class="word-break-all" :href="urlReferencias1" target="_blank">{{ urlReferencias1 }}</a>
    </p>
    <p>
      2. Silva, Gulnar Azevedo e et al. Evaluation of cervical cancer control actions within Brazil
      and its regions based on data recorded in the Brazilian Unified National Health System.
      Cadernos de Saúde Pública [online]. 2022, v. 38, n. 7 [Accessed 29 August 2022] , e00041722.
      Available from: <span class="word-break-all">&lt;{{ urlReferencias2 }}&gt;</span>.
      Epub 25 July 2022. ISSN 1678-4464
      <a class="word-break-all" :href="urlReferencias2" target="_blank">{{ urlReferencias2 }}</a>.
    </p>
    <p>
      3. Ribeiro CM, Dias MBK, Pla MAS, Correa FM, Russomano FB, Tomazelli JG. Parâmetros para a
      programação de procedimentos da linha de cuidado do câncer do colo do útero no Brasil. Cad
      Saúde Pública 2019; 35:e00183118.
    </p>
  </div>
</template>

<script>
export default {
  name: 'texto-explicativo-cobertura-coleta',
  data: () => ({
    urlReferencias1: 'https://www.inca.gov.br/sites/ufu.sti.inca.local/files//media/document/diretrizesparaorastreamentodocancerdocolodoutero_2016_corrigido.pdf',
    urlReferencias2: 'https://doi.org/10.1590/0102-311XPT041722',
  }),
};
</script>

<style lang="scss" scoped>
a { font-weight: bold; }

.word-break-all { word-break: break-all; }

.meta {
  font-weight: bold;
  &--boa { color: #0AB87F; }
  &--medio { color: #F8950E; }
  &--ruim { color: #D53C3C; }
}

.small-text { font-size: .875rem }
</style>
